import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import { useSelector } from "react-redux";

export const FormDigUsers = () => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const [rows, setRows] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionEmail, setActionEmail] = useState("");
  const [message, setMessage] = useState(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/admin/form-dig-users/list`,
        { currentSession },
        { withCredentials: true },
      );
      setRows(response.data.data || []);
    } catch (error) {
      setMessage({
        severity: "error",
        text:
          error?.response?.data?.message || "Users could not be loaded.",
      });
    } finally {
      setLoading(false);
    }
  }, [currentSession]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const invite = async (inviteEmail) => {
    setActionEmail(inviteEmail);
    setMessage(null);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/admin/form-dig-users/invite`,
        { email: inviteEmail, currentSession },
        { withCredentials: true },
      );
      setMessage({ severity: "success", text: response.data.message });
      setEmail("");
      await loadUsers();
    } catch (error) {
      setMessage({
        severity: "error",
        text:
          error?.response?.data?.message ||
          "Invitation could not be sent.",
      });
    } finally {
      setActionEmail("");
    }
  };

  const remove = async (removeEmail) => {
    setActionEmail(removeEmail);
    setMessage(null);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/admin/form-dig-users/remove`,
        { email: removeEmail, currentSession },
        { withCredentials: true },
      );
      setMessage({ severity: "success", text: response.data.message });
      await loadUsers();
    } catch (error) {
      setMessage({
        severity: "error",
        text: error?.response?.data?.message || "User could not be removed.",
      });
    } finally {
      setActionEmail("");
    }
  };

  const formatDate = (timestamp) =>
    timestamp ? new Date(timestamp * 1000).toLocaleString() : "—";

  const columns = [
    { field: "email", headerName: "Email", flex: 1, minWidth: 240 },
    { field: "status", headerName: "Status", width: 130 },
    {
      field: "invite_sent_at",
      headerName: "Invited",
      width: 190,
      valueFormatter: (value) => formatDate(value),
    },
    {
      field: "registered_at",
      headerName: "Registered",
      width: 190,
      valueFormatter: (value) => formatDate(value),
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      width: 230,
      renderCell: ({ row }) => (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ height: "100%" }}
        >
          {row.status === "invited" && (
            <Button
              size="small"
              onClick={() => invite(row.email)}
              disabled={Boolean(actionEmail)}
            >
              Resend
            </Button>
          )}
          <Button
            size="small"
            color="error"
            onClick={() => remove(row.email)}
            disabled={Boolean(actionEmail)}
          >
            Revoke
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Invite independent users who can access only the Form Uploader workflow.
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          fullWidth
          type="email"
          label="Email address"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <LoadingButton
          variant="contained"
          loading={actionEmail === email && Boolean(email)}
          disabled={!email || Boolean(actionEmail)}
          onClick={() => invite(email.trim().toLowerCase())}
          sx={{ minWidth: 150 }}
        >
          Send invite
        </LoadingButton>
      </Stack>
      {message && (
        <Alert severity={message.severity} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}
      <DataGrid
        autoHeight
        rows={rows}
        columns={columns}
        loading={loading}
        disableRowSelectionOnClick
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
      />
    </Box>
  );
};